import { NegotiationFilterRes } from "../../../services/interfaces/IVendorQuoteAssessment";

export type NavItem =
    | "quoteDetails"
    | "quoteSummary"
    | "renegotiateSummary"
    | "sKURatioAnalysis"
    | "districtComparison";

export type QuoteSummaryMode = "supplier" | "district" | "month";

export type QuoteValue = string;

export type MultiValue = string[];

export type SkuFiltersState = {
    commodity: string | null;
    districts: MultiValue;
    vendor: MultiValue;
    contractTypes: MultiValue;
    contractValidity: MultiValue;
    benchmark: string;
};

export type QuoteSummaryFiltersState = {
    commodity: string | null;
    districts: MultiValue;
    vendor: MultiValue;
    date: MultiValue;
    contractTypes: MultiValue;
    contractValidity: MultiValue;
    benchmark: string;
};

export type AllFiltersState = Omit<NegotiationFilterRes, "date">;

export type VendorQuoteAssessmentTab = {
    key: NavItem;
    label: string;
    trackingLabel: string;
};

export const INITIAL_ALL_FILTERS: AllFiltersState = {
    commodity: [],
    districts: [],
    vendor: [],
    quotation_no: [],
    month_year: [],
    contract_type: [],
    contract_validity: [],
    benchmark: [],
    indexUploaded: false,
};

export const VALID_TABS: NavItem[] = [
    "quoteDetails",
    "quoteSummary",
    "renegotiateSummary",
    "sKURatioAnalysis",
    "districtComparison",
];

export const VENDOR_QUOTE_ASSESSMENT_TABS: VendorQuoteAssessmentTab[] = [
    {
        key: "quoteSummary",
        label: "Quote Summary",
        trackingLabel: "Quote Summary",
    },
    {
        key: "renegotiateSummary",
        label: "Renegotiate Summary",
        trackingLabel: "Renegotiate Summary",
    },
    {
        key: "sKURatioAnalysis",
        label: "Ratio Analysis",
        trackingLabel: "SKU Ratio Analysis",
    },
    {
        key: "districtComparison",
        label: "District Comparison",
        trackingLabel: "District Comparison",
    },
];

export const VENDOR_QUOTE_ASSESSMENT_TABS_WITH_ACCESS: VendorQuoteAssessmentTab[] = [
    {
        key: "quoteDetails",
        label: "Quote Summary",
        trackingLabel: "Quote Summary",
    },
    {
        key: "quoteSummary",
        label: "Quote Details",
        trackingLabel: "Quote Details",
    },
    {
        key: "renegotiateSummary",
        label: "Renegotiate Summary",
        trackingLabel: "Renegotiate Summary",
    },
    {
        key: "sKURatioAnalysis",
        label: "Ratio Analysis",
        trackingLabel: "SKU Ratio Analysis",
    },
    {
        key: "districtComparison",
        label: "District Comparison",
        trackingLabel: "District Comparison",
    },
];

export const getInitialNavItem = (hiddenParam?: string): NavItem => {
    if (hiddenParam && VALID_TABS.includes(hiddenParam as NavItem)) {
        return hiddenParam as NavItem;
    }

    return "quoteDetails";
};

export const removeDateField = (
    filters: NegotiationFilterRes,
): AllFiltersState => {
    const { date, ...rest } = filters;
    return rest;
};

export const getMonthValues = (filters: Partial<NegotiationFilterRes>) =>
    filters.month_year?.map((item) => item.month) ?? [];

export const getContractTypeValues = (
    filters: Partial<NegotiationFilterRes>,
) => filters.contract_type?.map((item) => item.contract_type) ?? [];

/** Combine separate "Firm" and "Float" entries into a single "Firm/Float" display option. */
export const combineFirmFloat = (
    data: Array<{ contract_type: string }>,
): Array<{ contract_type: string }> => {
    const hasFirm = data.some((d) => d.contract_type === "Firm");
    const hasFloat = data.some((d) => d.contract_type === "Float");
    const others = data.filter(
        (d) => d.contract_type !== "Firm" && d.contract_type !== "Float",
    );
    const combined: Array<{ contract_type: string }> = [];
    if (hasFirm || hasFloat) {
        combined.push({ contract_type: "Firm/Float" });
    }
    return [...combined, ...others];
};

/** Expand the display value "Firm/Float" back to ["Firm", "Float"] for API calls. */
export const expandContractTypes = (types: string[]): string[] =>
    types.flatMap((t) => (t === "Firm/Float" ? ["Firm", "Float"] : [t]));

export const areSameSelections = (
    first: string[] | null | undefined,
    second: string[] | null | undefined,
) => {
    const left = [...(first ?? [])].sort();
    const right = [...(second ?? [])].sort();
    return left.toString() === right.toString();
};
